import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Tag } from "lucide-react";
import { SiteLayout } from "@/components/site/SiteLayout";
import { CTASection } from "@/components/site/CTASection";
import { ArticleCard } from "@/components/blog/ArticleCard";
import { getArticlesByTag, getAllTags } from "@/data/blogContent";
import { SEOHead } from "@/components/SEOHead";

const TagPage = () => {
  const { tag } = useParams<{ tag: string }>();
  const tagArticles = getArticlesByTag(tag || "");
  const allTags = getAllTags();

  return (
    <SiteLayout>
      <SEOHead
        title={`Articles sur "${tag}" – Blog Contrôle Technique`}
        description={`Tous les articles du blog Autosur Montreuil-sous-Bois sur le thème "${tag}". Conseils, réglementation et actualités.`}
        path={`/blog/tag/${tag}`}
        noindex
      />
      {/* Hero */}
      <section className="relative py-24 bg-gradient-to-br from-autosur-navy to-autosur-blue overflow-hidden">
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white text-sm font-body mb-6 border border-white/20">
              <Tag className="w-4 h-4" />
              <span>Tag</span>
            </div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
              #{tag}
            </h1>
            <p className="font-body text-white/80 text-lg">
              {tagArticles.length} article{tagArticles.length > 1 ? 's' : ''} avec ce tag
            </p>
          </motion.div>
        </div>
      </section>

      {/* Back link */}
      <div className="bg-gray-50 border-b">
        <div className="container mx-auto px-6 py-4">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-autosur-navy hover:text-autosur-blue font-heading font-semibold transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Retour au blog
          </Link>
        </div>
      </div>

      {/* Articles */}
      <section className="py-12 bg-background">
        <div className="container mx-auto px-6">
          {tagArticles.length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {tagArticles.map((article, index) => (
                <ArticleCard key={article.slug} article={article} index={index} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="font-body text-muted-foreground mb-6">
                Aucun article avec ce tag pour le moment.
              </p>
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 text-autosur-blue font-heading font-semibold hover:text-autosur-navy transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                Retour au blog
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Other tags */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-6">
          <h2 className="font-heading text-xl font-bold text-autosur-navy mb-6 text-center">
            Autres tags
          </h2>
          <div className="flex flex-wrap gap-2 justify-center max-w-3xl mx-auto">
            {allTags
              .filter(t => t !== tag)
              .map((t) => (
                <Link
                  key={t}
                  to={`/blog/tag/${t}`}
                  className="px-4 py-2 bg-white hover:bg-autosur-blue/10 text-autosur-navy hover:text-autosur-blue rounded-full text-sm font-body transition-colors border border-gray-200"
                >
                  #{t}
                </Link>
              ))}
          </div>
        </div>
      </section>

      <CTASection />
    </SiteLayout>
  );
};

export default TagPage;
